package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.Announcement
import com.evoapps.evoshift.domain.model.AnnouncementRead
import com.evoapps.evoshift.domain.model.ShiftNote
import kotlinx.coroutines.flow.Flow

/**
 * Team announcements and their read receipts (SH-118): `workplaces/{id}/announcements/{postId}`
 * and its `reads/{uid}`. Everyone working at the workplace reads the posts; its managers write
 * them and see the receipts (firestore.rules).
 */
interface AnnouncementRepository {
    /** The workplace's posts (newest first, at most [limit]). */
    fun observe(workplaceId: String, limit: Int = 100): Flow<List<Announcement>>

    /** Posts [announcement] (its id is created here when blank); the new id. */
    suspend fun post(announcement: Announcement): Result<String>

    suspend fun setPinned(workplaceId: String, announcementId: String, pinned: Boolean, editorId: String): Result<Unit>

    suspend fun delete(workplaceId: String, announcementId: String): Result<Unit>

    /** Which of [announcementIds] [userId] has read. */
    suspend fun readIds(workplaceId: String, announcementIds: Collection<String>, userId: String): Result<Set<String>>

    /** Records that [userId] read the post (once; later calls change nothing). */
    suspend fun markRead(workplaceId: String, announcementId: String, userId: String, readAt: String): Result<Unit>

    /** Everyone who read the post (managers). */
    suspend fun reads(workplaceId: String, announcementId: String): Result<List<AnnouncementRead>>
}

/**
 * Notes on shifts for the people working them (SH-118): `workplaces/{id}/shift_notes/{shiftId}`.
 * The person reads their own, the workplace's managers all of them.
 */
interface ShiftNoteRepository {
    /** The note on [shiftId], or null. Managers. */
    suspend fun get(workplaceId: String, shiftId: String): Result<ShiftNote?>

    /** The notes for [userId] at [workplaceId], kept current. */
    fun observeFor(workplaceId: String, userId: String): Flow<List<ShiftNote>>

    /** Saves [note] (replacing the shift's earlier one). */
    suspend fun save(note: ShiftNote): Result<Unit>

    suspend fun delete(workplaceId: String, shiftId: String): Result<Unit>
}
